import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazyWithRetry as lazy } from '@/lib/lazy-with-retry';
import { QueryClient, QueryCache, MutationCache, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from '@/components/ui/toaster';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageLoader } from '@/components/PageLoader';
import { handleQueryError } from '@/lib/query-error-handler';
import { captureQueryError } from '@/lib/sentry';
import { isBillingBlockedError } from '@/services/api';
import { Login } from './pages/Login';
import { NotFoundOrg } from './pages/NotFoundOrg';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SystemLayout } from './features/system/components/SystemLayout';
import { BrandingProvider, useBranding } from './features/branding';
import { BrandingLoadingScreen } from './features/branding/components/BrandingLoadingScreen';
import { BillingGate } from './features/billing/components';
import { TermsConsentGate } from './features/terms';

// Lazy load pages for better performance
const PipelinePage = lazy(() => import('./pages/Pipeline').then(m => ({ default: m.PipelinePage })));
const DashboardPage = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.DashboardPage })));
const ContactsPage = lazy(() => import('./pages/Contacts').then(m => ({ default: m.ContactsPage })));
const WhatsAppPage = lazy(() => import('./pages/WhatsApp'));
const ReportsPage = lazy(() => import('./pages/Reports').then(m => ({ default: m.ReportsPage })));
const AutomationsPage = lazy(() => import('./features/automations/pages').then(m => ({ default: m.AutomationsPage })));
const AutomationEditorPage = lazy(() => import('./features/automations/pages').then(m => ({ default: m.AutomationEditorPage })));
const AutomationDetailsPage = lazy(() => import('./features/automations/pages').then(m => ({ default: m.AutomationDetailsPage })));
const AutomationsHealthPage = lazy(() => import('./features/automations/pages').then(m => ({ default: m.AutomationsHealthPage })));
const FluxosListPage = lazy(() => import('./features/fluxos/pages').then(m => ({ default: m.FluxosListPage })));
const FluxoEditorPage = lazy(() => import('./features/fluxos/pages').then(m => ({ default: m.FluxoEditorPage })));
const CampaignsListPage = lazy(() => import('./features/whatsapp-campaigns/pages/CampaignsListPage').then(m => ({ default: m.CampaignsListPage })));
const CampaignWizardPage = lazy(() => import('./features/whatsapp-campaigns/pages/CampaignWizardPage').then(m => ({ default: m.CampaignWizardPage })));
const CampaignDetailPage = lazy(() => import('./features/whatsapp-campaigns/pages/CampaignDetailPage').then(m => ({ default: m.CampaignDetailPage })));
const SettingsPage = lazy(() => import('./features/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ApiDocsPage = lazy(() => import('./features/settings/pages/ApiDocsPage').then(m => ({ default: m.ApiDocsPage })));
const WebhooksDocsPage = lazy(() => import('./features/settings/pages/WebhooksDocsPage').then(m => ({ default: m.WebhooksDocsPage })));
const SystemRootRedirect = lazy(() => import('./features/system/components/SystemRootRedirect').then(m => ({ default: m.SystemRootRedirect })));
const OrgDashboardPage = lazy(() => import('./features/system/pages/OrgDashboardPage').then(m => ({ default: m.OrgDashboardPage })));
const MasterUsersPage = lazy(() => import('./features/system/pages/MasterUsersPage').then(m => ({ default: m.MasterUsersPage })));
const OrganizationsListPage = lazy(() => import('./features/system/pages/OrganizationsListPage').then(m => ({ default: m.OrganizationsListPage })));
const CreateOrganizationPage = lazy(() => import('./features/system/pages/CreateOrganizationPage').then(m => ({ default: m.CreateOrganizationPage })));
const OrganizationDetailPage = lazy(() => import('./features/system/pages/OrganizationDetailPage').then(m => ({ default: m.OrganizationDetailPage })));
const TasksPage = lazy(() => import('./features/tasks/pages/TasksPage').then(m => ({ default: m.TasksPage })));
const DealsPage = lazy(() => import('./pages/Deals').then(m => ({ default: m.DealsPage })));
const NotificationsPage = lazy(() => import('./features/notifications/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const TenantsListPage = lazy(() => import('./features/system/pages/TenantsListPage').then(m => ({ default: m.TenantsListPage })));
const CreateTenantPage = lazy(() => import('./features/system/pages/CreateTenantPage').then(m => ({ default: m.CreateTenantPage })));
const TenantDetailPage = lazy(() => import('./features/system/pages/TenantDetailPage').then(m => ({ default: m.TenantDetailPage })));
const PlansListPage = lazy(() => import('./features/system/pages/PlansListPage').then(m => ({ default: m.PlansListPage })));
const MagicLinkVerify = lazy(() => import('./pages/MagicLinkVerify').then(m => ({ default: m.MagicLinkVerify })));
const BillingPage = lazy(() => import('./pages/Billing').then(m => ({ default: m.BillingPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPasswordPage })));
const OptInPage = lazy(() => import('./pages/OptIn').then(m => ({ default: m.OptIn })));
const FeatureFlagsPage = lazy(() => import('./features/system/pages/FeatureFlagsPage').then(m => ({ default: m.FeatureFlagsPage })));

const sentryAwareQueryErrorHandler = (error: unknown): void => {
  // The synthetic paywall-block error (Epic #884 §3.1) is not a real failure —
  // the request never reached the backend. Drop it silently: no Sentry, no
  // toast. The PaywallScreen already communicates the blocked state.
  if (isBillingBlockedError(error)) {
    return;
  }
  captureQueryError(error);
  handleQueryError(error);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: sentryAwareQueryErrorHandler }),
  mutationCache: new MutationCache({ onError: sentryAwareQueryErrorHandler }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minuto
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: handleQueryError,
    },
  },
});

function AppRoutes() {
  const { isNotFound, isLoading } = useBranding();

  // Loading gate: block all route rendering until branding resolves.
  // This prevents the Login page from flashing before showing NotFoundOrg
  // for invalid organization slugs.
  if (isLoading) return <BrandingLoadingScreen />;

  if (isNotFound) {
    return <NotFoundOrg />;
  }

  return (
    <BrowserRouter>
      <BillingGate>
        <TermsConsentGate>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/magic" element={<MagicLinkVerify />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/opt-in" element={<OptInPage />} />

          {/* Protected Routes — All authenticated users */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales', 'viewer']}>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pipeline"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales', 'viewer']}>
                <PipelinePage />
              </ProtectedRoute>
            }
          />
          {/* `/contacts/:id` abre a lista com o modal do contato (ATR-621).
              UMA rota com param opcional, e não duas irmãs com o mesmo
              elemento: matches diferentes fazem o React Router remontar a
              página a cada abertura, e a aba Ativos/Arquivados (estado local)
              voltaria para "Ativos" — um contato arquivado sumiria da lista
              atrás do modal. */}
          <Route
            path="/contacts/:id?"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales', 'viewer']}>
                <ContactsPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes — Deals (admin, manager, sales, viewer) */}
          <Route
            path="/deals"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales', 'viewer']}>
                <DealsPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes — Tasks (admin, manager, sales, viewer) */}
          <Route
            path="/tasks"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales', 'viewer']}>
                <TasksPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes — Notifications (admin, manager, sales, viewer) */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales', 'viewer']}>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes — WhatsApp (admin, manager, sales) */}
          <Route
            path="/whatsapp"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales']}>
                <WhatsAppPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes — Reports (admin, manager) */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute roles={['admin', 'manager']}>
                <ReportsPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes — Automations (admin, manager) */}
          <Route
            path="/automations"
            element={
              <ProtectedRoute roles={['admin', 'manager']}>
                <AutomationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/automations/health"
            element={
              <ProtectedRoute roles={['admin', 'manager']}>
                <AutomationsHealthPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/automations/new"
            element={
              <ProtectedRoute roles={['admin', 'manager']}>
                <AutomationEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/automations/:id"
            element={
              <ProtectedRoute roles={['admin', 'manager']}>
                <AutomationDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/automations/:id/edit"
            element={
              <ProtectedRoute roles={['admin', 'manager']}>
                <AutomationEditorPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes — Fluxos (admin, manager) — gated by the tenant
              feature `automations` (Fluxos é a evolução das Automações) AND the
              global `fluxos_enabled` OPT_IN flag. */}
          <Route
            path="/fluxos"
            element={
              <ProtectedRoute roles={['admin', 'manager']} feature="automations" flag="fluxos_enabled">
                <FluxosListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fluxos/new"
            element={
              <ProtectedRoute roles={['admin', 'manager']} feature="automations" flag="fluxos_enabled">
                <FluxoEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fluxos/:id"
            element={
              <ProtectedRoute roles={['admin', 'manager']} feature="automations" flag="fluxos_enabled">
                <FluxoEditorPage />
              </ProtectedRoute>
            }
          />

          {/* Campanhas de WhatsApp (admin, manager): kill-switch global na rota;
              a feature de PLANO é checada dentro da página (card de cadeado por
              URL direta — o `featureGate` daqui redirecionaria). */}
          <Route
            path="/campanhas"
            element={
              <ProtectedRoute roles={['admin', 'manager']} flag="whatsapp_campaigns_enabled">
                <CampaignsListPage />
              </ProtectedRoute>
            }
          />
          {/* Wizard: `/nova` cria o rascunho ao entrar (segmento estático, ganha
              do `/campanhas/:id`); `/:id/editar` retoma o rascunho. */}
          <Route
            path="/campanhas/nova"
            element={
              <ProtectedRoute roles={['admin', 'manager']} flag="whatsapp_campaigns_enabled">
                <CampaignWizardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campanhas/:id/editar"
            element={
              <ProtectedRoute roles={['admin', 'manager']} flag="whatsapp_campaigns_enabled">
                <CampaignWizardPage />
              </ProtectedRoute>
            }
          />
          {/* Detalhe/relatório: rascunho redireciona para o editor. */}
          <Route
            path="/campanhas/:id"
            element={
              <ProtectedRoute roles={['admin', 'manager']} flag="whatsapp_campaigns_enabled">
                <CampaignDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes — Settings (admin, manager, sales — tabs filtered by role) */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales']}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Protected standalone — API docs (sem sidebar, aberta em nova aba a
              partir de Configurações → Integrações) */}
          <Route
            path="/api-docs"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales', 'viewer']}>
                <ApiDocsPage />
              </ProtectedRoute>
            }
          />

          {/* Protected standalone — Webhooks docs (sem sidebar, aberta em nova
              aba a partir de Configurações → Integrações) */}
          <Route
            path="/webhooks-docs"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales', 'viewer']}>
                <WebhooksDocsPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes — Billing. Reachable by ANY CRM role because the
              page itself gates on `isBillingOwner` (a non-admin can be the
              billing owner). Restricting to `admin` here caused an infinite
              redirect loop for a manager-owner during the paywall (BillingGate
              → /billing → admin-only reject → /dashboard → BillingGate → …).
              MASTER/ORG_OWNER are still auto-redirected to /system by
              ProtectedRoute. Non-owner CRM users see the restricted-access state. */}
          <Route
            path="/billing"
            element={
              <ProtectedRoute roles={['admin', 'manager', 'sales', 'viewer']} flag="billing_enabled">
                <BillingPage />
              </ProtectedRoute>
            }
          />

          {/* System Admin Routes — MASTER + ORG_OWNER */}
          <Route
            path="/system"
            element={
              <ProtectedRoute roles={['master', 'org_owner']}>
                <SystemLayout>
                  <SystemRootRedirect />
                </SystemLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system/dashboard"
            element={
              <ProtectedRoute roles={['master', 'org_owner']}>
                <SystemLayout>
                  <OrgDashboardPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system/masters"
            element={
              <ProtectedRoute roles={['master']}>
                <SystemLayout>
                  <MasterUsersPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system/feature-flags"
            element={
              <ProtectedRoute roles={['master']}>
                <SystemLayout>
                  <FeatureFlagsPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system/organizations"
            element={
              <ProtectedRoute roles={['master']}>
                <SystemLayout>
                  <OrganizationsListPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system/organizations/new"
            element={
              <ProtectedRoute roles={['master']}>
                <SystemLayout>
                  <CreateOrganizationPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system/organizations/:id"
            element={
              <ProtectedRoute roles={['master', 'org_owner']}>
                <SystemLayout>
                  <OrganizationDetailPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />

          {/* System - Tenant Management (ORG_OWNER only — MASTER manages orgs, not tenants) */}
          <Route
            path="/system/tenants"
            element={
              <ProtectedRoute roles={['org_owner']}>
                <SystemLayout>
                  <TenantsListPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system/tenants/new"
            element={
              <ProtectedRoute roles={['org_owner']}>
                <SystemLayout>
                  <CreateTenantPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system/tenants/:id"
            element={
              <ProtectedRoute roles={['org_owner']}>
                <SystemLayout>
                  <TenantDetailPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />

          {/* System - Plans Catalog (ORG_OWNER only) */}
          <Route
            path="/system/plans"
            element={
              <ProtectedRoute roles={['org_owner']}>
                <SystemLayout>
                  <PlansListPage />
                </SystemLayout>
              </ProtectedRoute>
            }
          />

        </Routes>
        </Suspense>
        </TermsConsentGate>
      </BillingGate>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) =>
        Sentry.captureException(error, {
          contexts: {
            react: { componentStack: errorInfo.componentStack ?? '' },
          },
        })
      }
    >
      <QueryClientProvider client={queryClient}>
        <BrandingProvider>
          <AppRoutes />
        </BrandingProvider>
        <Toaster />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
